/**
 * Domain vocabulary shared by the server and the client.
 *
 * These are the canonical unions from the implementation plan. The PostgreSQL
 * enums in `src/server/db/schema/enums.ts` use the same literals, so values
 * read from the database are assignable here without casting, and the Zod
 * contracts in `./contracts.ts` validate request input against the same lists.
 */

/**
 * The seven operational roles. Administration is a separate account capability.
 *
 * `parts_manager` (added 2026-09-16 at the dealership's request) holds exactly
 * the Parts powers — see {@link PARTS_ROLES}; it exists so a parts manager can
 * be told apart from the counter staff in the account list and activity
 * history, not to grant anything Parts lacks. Administration is still granted
 * separately (`isAdmin`).
 */
export const ROLES = [
  "parts",
  "parts_manager",
  "service_advisor",
  "technician",
  "service_manager",
  "shop_foreman",
  "dispatcher",
] as const;

export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/**
 * The roles that hold the Parts powers (spec §2 "Parts permissions"): create
 * and edit orders, verify and reverse receipts, print receipts, view and retry
 * arrival emails, read the assignee directory, start on the Parts workspace.
 * Every "is this Parts?" decision on the server and the client goes through
 * {@link isPartsRole} so the two roles can never drift apart.
 */
export const PARTS_ROLES = ["parts", "parts_manager"] as const satisfies readonly Role[];

export type PartsRole = (typeof PARTS_ROLES)[number];

export function isPartsRole(role: Role): role is PartsRole {
  return (PARTS_ROLES as readonly Role[]).includes(role);
}

/**
 * The words for each role, as the account list, the activity history and
 * the account emails all print them. One table for the server and the
 * client, so a role is never named two ways.
 */
export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  parts: "Parts",
  parts_manager: "Parts manager",
  service_advisor: "Service advisor",
  technician: "Technician",
  service_manager: "Service manager",
  shop_foreman: "Shop foreman",
  dispatcher: "Dispatcher",
};

export function roleLabel(role: Role): string {
  return ROLE_LABELS[role] ?? role;
}

/** Roles that receive every arrival email (in addition to the assigned advisor and technician). */
export const BROADCAST_RECIPIENT_ROLES = [
  "service_manager",
  "shop_foreman",
  "dispatcher",
] as const satisfies readonly Role[];

export const CASE_STATUSES = ["ordered", "partially_arrived", "arrived", "cancelled"] as const;

export type CaseStatus = (typeof CASE_STATUSES)[number];

export const PART_EXCEPTIONS = [
  "backordered",
  "wrong_part",
  "damaged_part",
  "cancelled_line",
] as const;

export type PartException = (typeof PART_EXCEPTIONS)[number];

/** Parts-workspace views first, then the shared-workspace views. */
export const CASE_LIST_VIEWS = [
  "work_queue",
  "arrived_48h",
  "needs_attention",
  "all",
  "history",
  "my_vehicles",
  "all_active",
  "waiting",
] as const;

export type CaseListView = (typeof CASE_LIST_VIEWS)[number];

/**
 * The wholesale side's lists (wholesale spec §4.2): Open (Ordered, Partially
 * arrived, Needs attention; oldest first), Arrived (inside its 36-hour
 * window; newest first), History (past the window, or cancelled), and every
 * order — the customer directory's per-customer history.
 */
export const WHOLESALE_LIST_VIEWS = ["open", "arrived", "history", "all"] as const;

export type WholesaleListView = (typeof WHOLESALE_LIST_VIEWS)[number];

/**
 * The two logical transition emails the product sends (spec §7, §9), and the
 * manual "parts ready" email a wholesale order sends its customer
 * (wholesale spec §5).
 */
export const EMAIL_EVENT_KINDS = ["arrival", "arrival_corrected", "wholesale_ready"] as const;

export type EmailEventKind = (typeof EMAIL_EVENT_KINDS)[number];

/** The kinds the arrival outbox renders and enqueues: a case's two transitions. */
export type ArrivalEmailKind = Extract<EmailEventKind, "arrival" | "arrival_corrected">;

/** Outbox lifecycle of a logical email event and of each recipient delivery. */
export const EMAIL_STATES = ["pending", "sending", "sent", "failed"] as const;

export type EmailState = (typeof EMAIL_STATES)[number];

/**
 * The authenticated principal every service method receives. Built by the
 * session middleware from the stored user row — never from request input.
 */
export type Actor = Readonly<{
  userId: string;
  role: Role;
  isAdmin: boolean;
}>;
